//////////////////////////////////////////////////////////////////////////////
//
//  영상 시청 이력 목록 화면
//
//  작성자: MediInStep Team
//  생성일: 2025-12-26
//
//  설명: 영상 시청 이력 목록 조회, 필터링, 페이징 기능
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import { formatLocalDate, formatLocalTime } from "utils/dateFormatter";

// Material Dashboard 2 React example components
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import BasicTable from "ScreenLayouts/TableLayout/BasicTableLayout/BasicTable";

// Network
import { request } from "app/network";
import { useMaterialUIController } from "context";
import type { ExerciseVideoWatchHistoryDTO } from "app/network/api/exerciseVideoWatchHistory";

interface VideoWatchHistoryState {
    records: any[];
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    loading: boolean;
    searchTerm: string;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_video_watch_history_page_size";

class VideoWatchHistoryScreenClass extends Component<any, VideoWatchHistoryState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 10;

        this.state = {
            records: [],
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            loading: false,
            searchTerm: "",
        };
    }

    componentDidMount() {
        this.loadRecords();
    }

    loadRecords = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                SearchTerm: this.state.searchTerm || undefined,
            };

            const res = await request("ExerciseVideoWatchHistory.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const records = (pagedList.items || []).map((record: ExerciseVideoWatchHistoryDTO) => {
                    // 날짜 포맷 - Raw Data 전달
                    const watchDate = record.watchDate || null;
                    const crtDt = record.crtDt || null;

                    // 완료율 표시
                    const completionRate = record.completionPercentage
                        ? `${record.completionPercentage.toFixed(1)}%`
                        : "-";

                    // 시청 시간 포맷 (초 -> 분:초)
                    const watchTime = record.maxWatchedSeconds
                        ? `${Math.floor(record.maxWatchedSeconds / 60)}:${String(record.maxWatchedSeconds % 60).padStart(2, '0')}`
                        : "-";

                    return {
                        id: record.watchSeqId,
                        watchDate,
                        crtDt,
                        userName: record.userName || "-",
                        userEmail: record.userEmail || "-",
                        videoTitle: record.videoTitle || "-",
                        watchTime,
                        completionRate,
                        isCompleted: record.isCompleted,
                        isRewarded: record.isRewarded,
                        rewardPoints: record.rewardPoints || 0,
                    };
                });

                this.setState({
                    records,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("영상 시청 이력 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadRecords();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadRecords();
        });
    };

    handleSearch = (searchValue: string) => {
        this.setState({ searchTerm: searchValue, pageNumber: 1 }, () => {
            this.loadRecords();
        });
    };

    render() {
        const { records, pageSize, loading, totalCount, pageNumber } = this.state;

        const columns = [
            {
                Header: "시청일시",
                accessor: "watchDate",
                align: "center",
                width: "10%",
                Cell: ({ row }: any) => (
                    <MDBox lineHeight={1}>
                        <MDTypography display="block" variant="caption" fontWeight="medium">
                            {formatLocalDate(row.original.crtDt || row.original.watchDate)}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" fontWeight="regular">
                            {formatLocalTime(row.original.crtDt || row.original.watchDate)}
                        </MDTypography>
                    </MDBox>
                )
            },
            { Header: "사용자", accessor: "userName", align: "left", width: "12%" },
            { Header: "이메일", accessor: "userEmail", align: "left", width: "18%" },
            {
                Header: "영상 제목",
                accessor: "videoTitle",
                align: "left",
                width: "20%",
                Cell: ({ row }: any) => {
                    const title = row.original.videoTitle;
                    if (!title) return "-";
                    return title.length > 25 ? `${title.substring(0, 25)}...` : title;
                }
            },
            { Header: "시청 시간", accessor: "watchTime", align: "center", width: "10%" },
            {
                Header: "완료율",
                accessor: "completionRate",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const rate = row.original.completionRate;
                    const isCompleted = row.original.isCompleted;
                    return (
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            color={isCompleted ? "success" : "text"}
                        >
                            {rate}
                        </MDTypography>
                    );
                }
            },
            {
                Header: "완료",
                accessor: "isCompleted",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const isCompleted = row.original.isCompleted;
                    return (
                        <MDBadge
                            badgeContent={isCompleted ? "완료" : "미완료"}
                            color={isCompleted ? "success" : "secondary"}
                            variant="gradient"
                            size="sm"
                        />
                    );
                }
            },
            {
                Header: "보상",
                accessor: "isRewarded",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const isRewarded = row.original.isRewarded;
                    if (isRewarded === null || isRewarded === undefined) {
                        return <MDTypography variant="caption">-</MDTypography>;
                    }
                    return (
                        <MDBadge
                            badgeContent={isRewarded ? "지급" : "미지급"}
                            color={isRewarded ? "info" : "secondary"}
                            variant="gradient"
                            size="sm"
                        />
                    );
                }
            },
            {
                Header: "포인트",
                accessor: "rewardPoints",
                align: "right",
                width: "8%",
                Cell: ({ row }: any) => {
                    const points = row.original.rewardPoints;
                    if (!points) return <MDTypography variant="caption">-</MDTypography>;
                    return (
                        <MDTypography variant="caption" fontWeight="bold" color="info">
                            {points.toLocaleString()}P
                        </MDTypography>
                    );
                }
            },
        ];

        const rows = records;

        const totalPages = Math.ceil(totalCount / pageSize);
        const pageCountArray = Array.from({ length: totalPages }, (_, i) => i);
        const currentPageIndex = pageNumber - 1;
        const pageTenNumber = Math.floor(currentPageIndex / 10) * 10;

        const paginationCriteria = {
            PageNumber: currentPageIndex,
            PageCount: pageCountArray,
            PageTenNumber: pageTenNumber,
            totalCount: totalCount,
        };

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} position="relative">
                    <Card sx={{ "& table": { tableLayout: "fixed", width: "100%" } }}>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display={"flex"} alignContent="center">
                                    <Icon fontSize={"medium"}>play_circle</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        영상 시청 이력 ( VIDEO WATCH HISTORY )
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                            <MDTypography variant="button" color="text" mt={1}>
                                영상 시청 이력 목록 (전체: {totalCount}개)
                            </MDTypography>
                        </MDBox>
                        <BasicTable
                            canSearch={true}
                            onSearch={this.handleSearch}
                            criteria={paginationCriteria}
                            storePageSize={pageSize}
                            goPage={this.handlePageChange}
                            setStorePageSize={this.handlePageSizeChange}
                            table={{ columns, rows }}
                            isSorted={false}
                            entriesPerPage={{
                                defaultValue: pageSize,
                                entries: [5, 10, 15, 20, 25, 30, 40, 50],
                            }}
                            showTotalEntries={true}
                            noEndBorder
                        />
                        {loading && (
                            <MDBox
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="rgba(0, 0, 0, 0.3)"
                                sx={{ pointerEvents: "none" }}
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        )}
                    </Card>
                </MDBox>
                <Footer />
            </DashboardLayout>
        );
    }
}

function VideoWatchHistoryScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return <VideoWatchHistoryScreenClass {...props} darkMode={darkMode} />;
}

export default VideoWatchHistoryScreen;
